import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchSelect from "@/components/common/SearchSelect";
import { Textarea } from "@/components/ui/textarea";
import { fmtINR, today, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Download, ArrowRightLeft, XCircle, Upload, Image as ImgIcon } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const IT_BLOCKS = [
  { label: "Buildings (residential) — 5%", value: 5 },
  { label: "Buildings (commercial) — 10%", value: 10 },
  { label: "Furniture & Fittings — 10%", value: 10 },
  { label: "Plant & Machinery — 15%", value: 15 },
  { label: "Motor Vehicles — 15%", value: 15 },
  { label: "Ships — 20%", value: 20 },
  { label: "Computers & Software — 40%", value: 40 },
  { label: "Books (professional) — 40%", value: 40 },
];

const CLASSIFICATIONS = [
  "Land", "Buildings", "Plant & Machinery", "Furniture & Fixtures",
  "Vehicles", "Office Equipment", "Computers", "Intangible Assets",
];

export default function FixedAssets() {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [xferOpen, setXferOpen] = useState(false);
  const [xferAsset, setXferAsset] = useState(null);
  const [xferForm, setXferForm] = useState({ to_branch_id: "", to_location: "", to_custodian: "", reason: "" });
  const [lightbox, setLightbox] = useState(null); // { url, asset }
  const [photoCache, setPhotoCache] = useState({}); // { attId: blob-url }
  const [orgName, setOrgName] = useState("");
  const [viewAsset, setViewAsset] = useState(null);
  const emptyForm = {
    asset_code: "", name: "", serial_number: "", description: "", classification: "Plant & Machinery",
    category: "plant", location: "", custodian: "", department: "",
    cost: 0, purchase_date: today(), put_to_use_date: today(), salvage_value: 0,
    book_useful_life_years: 5, book_method: "SLM",
    tax_method: "WDV", tax_block_rate: 15,
    branch_id: "",
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => setRows((await api.get("/assets")).data);
  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data));
    // Pull org name for the watermark on the photo lightbox
    api.get("/organizations/mine").then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data?.orgs || [];
      const currentId = list?.[0]?.id ? list[0].id : null;
      setOrgName(list?.find?.((o) => o.is_current)?.name || list?.[0]?.name || "");
    }).catch(() => {});
  }, []);

  const create = async () => {
    if (!form.asset_code || !form.name) return toast.error("Code and name required");
    try {
      await api.post("/assets", { ...form, branch_id: form.branch_id || branches[0]?.id });
      toast.success("Asset added"); setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runDep = async (id) => {
    try { await api.post(`/assets/${id}/depreciate`); toast.success("Depreciation posted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openTransfer = (a) => {
    setXferAsset(a);
    setXferForm({ to_branch_id: a.branch_id, to_location: a.location || "", to_custodian: a.custodian || "", reason: "" });
    setXferOpen(true);
  };
  const doTransfer = async () => {
    try {
      await api.post(`/assets/${xferAsset.id}/transfer`, xferForm);
      toast.success("Asset transferred"); setXferOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const dispose = async (a) => {
    const val = window.prompt(`Dispose "${a.name}" — disposal value (₹)?`, "0");
    if (val === null) return;
    try {
      await api.post(`/assets/${a.id}/dispose`, { disposal_value: Number(val), disposal_date: today() });
      toast.success("Asset disposed"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const uploadPhoto = async (a, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/attachments/fixed_asset/${a.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/assets/${a.id}`, { photo_attachment_id: r.data.id });
      // Prime cache
      setPhotoCache((c) => ({ ...c, [r.data.id]: undefined }));
      toast.success("Photo linked"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  // Lazy-fetch photo blob for a given attachment id, cache & return URL
  const fetchPhoto = async (attId) => {
    if (photoCache[attId]) return photoCache[attId];
    try {
      const r = await api.get(`/attachments/download/${attId}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setPhotoCache((c) => ({ ...c, [attId]: url }));
      return url;
    } catch { return null; }
  };

  // Preload photos for rows that have one — non-blocking
  useEffect(() => {
    rows.forEach((a) => {
      if (a.photo_attachment_id && !photoCache[a.photo_attachment_id]) fetchPhoto(a.photo_attachment_id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const openLightbox = async (a) => {
    if (!a.photo_attachment_id) return;
    const url = await fetchPhoto(a.photo_attachment_id);
    if (url) setLightbox({ url, asset: a });
  };

  const downloadFAR = async () => {
    try {
      const r = await api.get("/assets/far.xlsx", { params: { as_of: today() }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const el = document.createElement("a"); el.href = url; el.download = `FAR_${today()}.xlsx`; el.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const postMonthlyDep = async () => {
    const month = window.prompt("Post depreciation for which month? (YYYY-MM)", today().slice(0, 7));
    if (!month) return;
    if (!/^\d{4}-\d{2}$/.test(month)) return toast.error("Invalid format, use YYYY-MM");
    try {
      const r = await api.post("/assets/depreciate-monthly", { month });
      const { posted, skipped, total_posted } = r.data;
      toast.success(`${posted.length} assets · Total ${fmtINR(total_posted)} posted for ${month} (${skipped.length} skipped)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const totalCost = rows.reduce((s, r) => s + (r.cost || 0), 0);
  const totalBookNBV = rows.reduce((s, r) => s + (r.cost - (r.book_accumulated_dep || 0)), 0);
  const totalTaxNBV = rows.reduce((s, r) => s + (r.cost - (r.tax_accumulated_dep || 0)), 0);
  const totalDiff = totalBookNBV - totalTaxNBV;

  const branchOptions = useMemo(() => branches.map((b) => ({ value: b.id, label: b.name })), [branches]);
  const classifOptions = CLASSIFICATIONS.map((c) => ({ value: c, label: c }));
  const itBlockOptions = IT_BLOCKS.map((b) => ({ value: String(b.value), label: b.label }));

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="assets-page">
      <PageHeader eyebrow="Compliance" title="Fixed Assets"
        description="Dual depreciation — Companies Act Schedule II (book) + Income Tax Act WDV (tax). Location · custodian · photos · transfers · downloadable FAR."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={postMonthlyDep} className="rounded-sm gap-1.5 border-accent text-accent hover:bg-accent/10" data-testid="post-monthly-dep">Post Monthly Depreciation</Button>
            <Button variant="outline" onClick={downloadFAR} className="rounded-sm gap-1.5" data-testid="download-far"><Download className="w-3.5 h-3.5" /> FAR Excel</Button>
            <Button onClick={() => setOpen(true)} className="rounded-sm bg-accent text-white" data-testid="new-asset-btn">+ Asset</Button>
          </div>
        } />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Gross Block</div><div className="metric-value text-2xl mt-2">{fmtINR(totalCost)}</div></div>
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Book NBV</div><div className="metric-value text-2xl mt-2">{fmtINR(totalBookNBV)}</div></div>
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Tax NBV</div><div className="metric-value text-2xl mt-2">{fmtINR(totalTaxNBV)}</div></div>
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Book–Tax Diff</div><div className={`metric-value text-2xl mt-2 ${totalDiff >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtINR(totalDiff)}</div></div>
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead>
            <tr>
              <th style={{ width: 60 }}>Photo</th>
              <th>Code</th><th>Name</th><th>Serial</th><th>Location</th><th>Custodian</th>
              <th className="text-right">Cost</th>
              <th className="text-right">Book NBV</th><th className="text-right">Tax NBV</th>
              <th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="11" className="text-center text-muted-foreground py-8">No assets yet</td></tr>}
            {rows.map((a) => (
              <tr key={a.id} onClick={() => setViewAsset(a)} className="cursor-pointer hover:bg-muted/40" data-testid={`asset-row-${a.asset_code}`}>
                <td className="p-1" onClick={(e) => e.stopPropagation()}>
                  {a.photo_attachment_id ? (
                    <button onClick={() => openLightbox(a)} className="w-10 h-10 rounded-sm overflow-hidden border hover:ring-2 hover:ring-accent transition" data-testid={`asset-thumb-${a.asset_code}`}>
                      {photoCache[a.photo_attachment_id]
                        ? <img src={photoCache[a.photo_attachment_id]} alt={a.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full bg-muted animate-pulse" />}
                    </button>
                  ) : (
                    <div className="w-10 h-10 rounded-sm border border-dashed flex items-center justify-center text-muted-foreground">
                      <ImgIcon className="w-4 h-4 opacity-40" />
                    </div>
                  )}
                </td>
                <td className="font-mono text-xs">{a.asset_code}</td>
                <td>{a.name}<div className="text-[10px] text-muted-foreground">{a.classification || a.category}</div></td>
                <td className="font-mono text-xs">{a.serial_number || "—"}</td>
                <td className="text-xs">{a.location || "—"}</td>
                <td className="text-xs">{a.custodian || "—"}</td>
                <td className="text-right tabular-nums">{fmtINR(a.cost)}</td>
                <td className="text-right tabular-nums font-medium">{fmtINR(a.depreciation?.book_wdv)}</td>
                <td className="text-right tabular-nums">{fmtINR(a.depreciation?.tax_wdv)}</td>
                <td>
                  {a.status === "disposed"
                    ? <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700">DISPOSED</span>
                    : <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">ACTIVE</span>}
                </td>
                <td className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex gap-1">
                    <label className="cursor-pointer inline-flex items-center h-7 px-1.5 rounded-sm hover:bg-muted" title="Upload photo">
                      <ImgIcon className="w-3.5 h-3.5 text-muted-foreground" />
                      <input type="file" accept="image/*" hidden onChange={(e) => uploadPhoto(a, e.target.files?.[0])} data-testid={`asset-photo-${a.asset_code}`} />
                    </label>
                    <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => openTransfer(a)} disabled={a.status === "disposed"} data-testid={`asset-transfer-${a.asset_code}`}><ArrowRightLeft className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => runDep(a.id)} disabled={a.status === "disposed"} data-testid={`asset-dep-${a.asset_code}`}>Post dep</Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500" onClick={() => dispose(a)} disabled={a.status === "disposed"} data-testid={`asset-dispose-${a.asset_code}`}><XCircle className="w-3.5 h-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create asset sheet */}
      <Sheet open={!!viewAsset} onOpenChange={(o) => !o && setViewAsset(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none overflow-y-auto space-y-4" data-testid="asset-view-drawer">
          <SheetHeader className="pb-3 border-b">
            <SheetTitle className="font-heading flex items-center gap-3">
              <span className="text-[10px] font-mono uppercase text-accent px-2 py-0.5 rounded-sm bg-accent/10">{viewAsset?.asset_code}</span>
              {viewAsset?.name}
            </SheetTitle>
          </SheetHeader>
          {viewAsset && (
            <div className="text-sm space-y-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 border rounded-sm p-4 bg-muted/20">
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Classification</div><div className="mt-1">{viewAsset.classification || viewAsset.category}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Serial #</div><div className="mt-1 font-mono">{viewAsset.serial_number || "—"}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Location</div><div className="mt-1">{viewAsset.location || "—"}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Custodian</div><div className="mt-1">{viewAsset.custodian || "—"}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Department</div><div className="mt-1">{viewAsset.department || "—"}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Purchase Date</div><div className="mt-1 font-mono">{fmtDate(viewAsset.purchase_date)}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Put to Use</div><div className="mt-1 font-mono">{fmtDate(viewAsset.put_to_use_date)}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Status</div><div className="mt-1 font-mono uppercase text-xs">{viewAsset.status}</div></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="border rounded-sm p-3 bg-slate-500/5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Cost</div><div className="mt-1 text-lg font-bold tabular-nums">{fmtINR(viewAsset.cost || 0)}</div></div>
                <div className="border rounded-sm p-3 bg-emerald-500/5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Book NBV (SLM)</div><div className="mt-1 text-lg font-bold tabular-nums">{fmtINR(viewAsset.depreciation?.book_wdv || 0)}</div><div className="text-[10px] mt-0.5 text-muted-foreground">Method: {viewAsset.book_method} · Life {viewAsset.book_useful_life_years}y</div></div>
                <div className="border rounded-sm p-3 bg-blue-500/5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Tax NBV (WDV)</div><div className="mt-1 text-lg font-bold tabular-nums">{fmtINR(viewAsset.depreciation?.tax_wdv || 0)}</div><div className="text-[10px] mt-0.5 text-muted-foreground">Block: {viewAsset.tax_block_rate}%</div></div>
              </div>
              <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Salvage value</div><div className="mt-1 tabular-nums">{fmtINR(viewAsset.salvage_value || 0)}</div></div>
              {viewAsset.description && <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Description</div><div className="mt-1 text-xs">{viewAsset.description}</div></div>}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Create asset sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none space-y-3 overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading">New Fixed Asset</SheetTitle></SheetHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Asset Code *</Label><Input value={form.asset_code} onChange={(e) => setForm({ ...form, asset_code: e.target.value })} className="rounded-sm mt-1" data-testid="asset-code" /></div>
              <div><Label>Serial #</Label><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="rounded-sm mt-1" data-testid="asset-serial" /></div>
            </div>
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" data-testid="asset-name" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" rows={2} data-testid="asset-description" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Classification (Sch III)</Label>
                <SearchSelect options={classifOptions} value={form.classification} onChange={(v) => setForm({ ...form, classification: v })} testId="asset-classification" />
              </div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plant">Plant & Machinery</SelectItem>
                    <SelectItem value="computer">Computer & IT</SelectItem>
                    <SelectItem value="furniture">Furniture</SelectItem>
                    <SelectItem value="vehicle">Vehicle</SelectItem>
                    <SelectItem value="building">Building</SelectItem>
                    <SelectItem value="office">Office Equipment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Branch</Label>
                <SearchSelect options={branchOptions} value={form.branch_id} onChange={(v) => setForm({ ...form, branch_id: v })} testId="asset-branch" placeholder="Select branch" />
              </div>
              <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. Shop-floor Bay-A" data-testid="asset-location" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Custodian</Label><Input value={form.custodian} onChange={(e) => setForm({ ...form, custodian: e.target.value })} className="rounded-sm mt-1" placeholder="Employee name" data-testid="asset-custodian" /></div>
              <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="rounded-sm mt-1" placeholder="Production, Admin…" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cost (₹) *</Label><Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: Number(e.target.value) })} className="rounded-sm mt-1" data-testid="asset-cost" /></div>
              <div><Label>Salvage (₹)</Label><Input type="number" value={form.salvage_value} onChange={(e) => setForm({ ...form, salvage_value: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Purchase date</Label><Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="rounded-sm mt-1" /></div>
              <div><Label>Put to use</Label><Input type="date" value={form.put_to_use_date} onChange={(e) => setForm({ ...form, put_to_use_date: e.target.value })} className="rounded-sm mt-1" /></div>
            </div>
            <div className="border-t pt-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Book — Companies Act Schedule II</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Useful life (yrs)</Label><Input type="number" value={form.book_useful_life_years} onChange={(e) => setForm({ ...form, book_useful_life_years: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
                <div><Label>Method</Label>
                  <Select value={form.book_method} onValueChange={(v) => setForm({ ...form, book_method: v })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="SLM">SLM</SelectItem><SelectItem value="WDV">WDV</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="border-t pt-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Tax — Income Tax Act (WDV block)</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Method</Label>
                  <Select value={form.tax_method} onValueChange={(v) => setForm({ ...form, tax_method: v })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="WDV">WDV</SelectItem><SelectItem value="SLM">SLM</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Block rate %</Label>
                  <SearchSelect options={itBlockOptions} value={String(form.tax_block_rate)} onChange={(v) => setForm({ ...form, tax_block_rate: Number(v) })} testId="asset-tax-block" placeholder="Select IT block" />
                </div>
              </div>
            </div>
          </div>
          <Button onClick={create} className="w-full rounded-sm bg-accent text-white" data-testid="asset-save">Save Asset</Button>
        </SheetContent>
      </Sheet>

      {/* Transfer sheet */}
      <Sheet open={xferOpen} onOpenChange={setXferOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader><SheetTitle className="font-heading">Transfer Asset · {xferAsset?.asset_code}</SheetTitle></SheetHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              From: <b>{branches.find((b) => b.id === xferAsset?.branch_id)?.name || "—"}</b> · {xferAsset?.location || "—"} · {xferAsset?.custodian || "—"}
            </div>
            <div><Label>To Branch</Label>
              <SearchSelect options={branchOptions} value={xferForm.to_branch_id} onChange={(v) => setXferForm({ ...xferForm, to_branch_id: v })} testId="xfer-branch" />
            </div>
            <div><Label>To Location</Label><Input value={xferForm.to_location} onChange={(e) => setXferForm({ ...xferForm, to_location: e.target.value })} className="rounded-sm mt-1" data-testid="xfer-location" /></div>
            <div><Label>To Custodian</Label><Input value={xferForm.to_custodian} onChange={(e) => setXferForm({ ...xferForm, to_custodian: e.target.value })} className="rounded-sm mt-1" data-testid="xfer-custodian" /></div>
            <div><Label>Reason</Label><Textarea value={xferForm.reason} onChange={(e) => setXferForm({ ...xferForm, reason: e.target.value })} className="rounded-sm mt-1" rows={2} data-testid="xfer-reason" /></div>
          </div>
          <Button onClick={doTransfer} className="w-full rounded-sm bg-accent text-white" data-testid="xfer-save">Transfer</Button>
        </SheetContent>
      </Sheet>
      {/* Photo lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          {lightbox && (
            <div className="bg-black relative">
              <img src={lightbox.url} alt={lightbox.asset.name} className="w-full max-h-[70vh] object-contain" data-testid="asset-lightbox-img" />
              {/* Audit-grade watermark (diagonal, tiled, low-opacity) */}
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden">
                <div
                  className="text-white/25 font-mono text-2xl font-bold whitespace-nowrap select-none tracking-widest"
                  style={{ transform: "rotate(-30deg)", textShadow: "0 0 12px rgba(0,0,0,0.5)" }}
                  data-testid="asset-watermark">
                  PROPERTY OF {(orgName || "").toUpperCase()} · CAPEX {lightbox.asset.purchase_date || "—"} · {lightbox.asset.asset_code}
                </div>
              </div>
              <div className="bg-card p-4 border-t">
                <div className="font-heading text-base">{lightbox.asset.name}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  <span className="font-mono">{lightbox.asset.asset_code}</span>
                  {lightbox.asset.serial_number && <> · Serial <span className="font-mono">{lightbox.asset.serial_number}</span></>}
                  {lightbox.asset.location && <> · {lightbox.asset.location}</>}
                  {lightbox.asset.custodian && <> · Custodian {lightbox.asset.custodian}</>}
                  {lightbox.asset.purchase_date && <> · Capex {lightbox.asset.purchase_date}</>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
